import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as UrlTemplate from 'url-template';

/**
 * Initialise the locale service BEFORE the root application component is
 * loaded.  Simply add the module to the root module, and the initialiser
 * will be automatically added.
 */
@Injectable({
  providedIn: 'root'
})
export class LMLocaleInitializerService {
  private _localeData: any;
  private defaultLocale = 'en-US';
  private bannerUrl: string;
  private culturesPath: string;

  /**
   * Constructor.
   *
   * @param locale the locale being displayed.
   * @param baseHref the base url of the web app
   */
  constructor(
    private http: HttpClient) {
    this.culturesPath = CulturesPath;
    this.bannerUrl = this.getBannerUrl(bannerSpecUrl);

    const delimiter = this.bannerUrl.indexOf('?') > -1 ? '&' : '?';
    this.bannerUrl += delimiter + 'requestType=localeData';
  }

  public getBannerUrl(url: string) {
    const urlCompiledTemplate = UrlTemplate.parse(url);
    return urlCompiledTemplate.expand({
      urlPdlOrDa: LandmarkContext.urlPdlOrDa,
      webApp: LandmarkContext.webApp
    });
  }

  /**
   * Initializes the locale for this web application.
   */
  async initialize(): Promise<boolean> {
    this._localeData = null;

    Soho.Locale.culturesPath = this.culturesPath;

    return new Promise((resolve, reject) => {
      this.http
        .get(this.bannerUrl)
        .toPromise()
        .then((data: any) => {
          this._localeData = data;
          const locale = this.getLocale(data);

          Soho.Locale.set(locale)
            .done(() => {
              // transform browserMessages into format for Soho.Locale
              const messages: { [ key: string ]: { id: string, value: string } } = {};
              data.browserMessages.forEach((message: { messageId: string, messageValue: string }) => {
                messages[ message.messageId ] = {
                  id: message.messageId,
                  value: message.messageValue
                };
              });

              const output = 'Locale set successfully';
              let errorMsg = '';

              try {
                Soho.Locale.extendTranslations(Soho.Locale.currentLanguage.name, messages);
              } catch (e) {
                errorMsg = 'Unable to extend translations to Soho.Locale';
                console.error(errorMsg + ' ' + locale);
                console.error(e);
              }

              resolve(true);
              console.log([ output + ((errorMsg.length > 0) ? ' - ' + errorMsg : ''), locale ]);
            })
            .fail(() => {
              console.log([ 'Locale set failed', locale ]);

              if (locale !== this.defaultLocale) {
                console.log([ 'Loading default locale', this.defaultLocale ]);

                Soho.Locale.set(this.defaultLocale)
                  .done(() => {
                    resolve(true);
                    console.log([ 'Default Locale set successfully', this.defaultLocale ]);
                  })
                  .fail(() => {
                    reject(true);
                    console.log([ 'Default Locale set failed', this.defaultLocale ]);
                  });
              }
            });
        })
        .catch((error: any) => {
          console.error(error.message, [error.details, error.status]);
          reject(true);
        });
    });
  }

  private getLocale(data): string {
    let locale = null;

    if (this.hasIsoLocale) {
      locale = LandmarkContext.contextSessionKeys[ 'csk.IsoLocale' ];
    } else if (data && data.hasOwnProperty('locale')) {
      locale = data.locale;
    }

    if (!locale) {
      locale = this.defaultLocale;
    }

    // maintain backwards compatibility with 3.5 (csk.IsoLocale=xx_ZZ)
    locale = locale.replace('_', '-');
    return locale;
  }

  private get hasIsoLocale(): boolean {
    return !!(LandmarkContext &&
      LandmarkContext.contextSessionKeys &&
      LandmarkContext.contextSessionKeys[ 'csk.IsoLocale' ]
    );
  }
}

export function LMLocaleInitializerFactory(service: LMLocaleInitializerService) {
  return (() => service.initialize());
}
